import { Box, Button, Flex, Group, RadioCard, RadioCardValueChangeDetails } from '@chakra-ui/react';
import { ReferenceType } from '@floating-ui/react';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { CSSProperties, memo, RefObject, useCallback } from 'react';

import { expand, getAttributeDescription, ProductDetails, ProductVariant } from 'helpers/fakeData';

const VariantDescription = memo(({ variant }: { variant: ProductVariant }) => {
  const attributeDescriptions = variant.attributeIds?.map((attributeId) =>
    getAttributeDescription(attributeId),
  );
  return <RadioCard.ItemDescription>{attributeDescriptions?.join(', ')}</RadioCard.ItemDescription>;
});

const VariantSelection = memo(
  ({
    product,
    onSelect,
    defaultVariantId,
  }: {
    product: ProductDetails;
    onSelect: (variant: ProductVariant) => void;
    defaultVariantId: string;
  }) => {
    const variants = expand([product]);

    const handleSelect = useCallback(
      (details: RadioCardValueChangeDetails) => {
        const variantId = details.value;
        const variant = variants.find((v) => v.id === variantId);
        if (variant == null) {
          return;
        }
        onSelect(variant);
      },
      [onSelect, variants],
    );

    return (
      <Flex>
        <RadioCard.Root
          defaultValue={defaultVariantId}
          gap="4"
          maxW="sm"
          onValueChange={handleSelect}
        >
          <Group attached orientation="vertical">
            {variants.map((variant) => (
              <RadioCard.Item
                key={variant.id}
                value={variant.id}
                width="full"
                borderColor="border.light"
              >
                <RadioCard.ItemHiddenInput />
                <RadioCard.ItemControl>
                  <RadioCard.ItemIndicator borderColor="border.light" />
                  <RadioCard.ItemContent>
                    <RadioCard.ItemText>{variant.name}</RadioCard.ItemText>
                    <VariantDescription variant={variant} />
                  </RadioCard.ItemContent>
                </RadioCard.ItemControl>
              </RadioCard.Item>
            ))}
          </Group>
        </RadioCard.Root>
      </Flex>
    );
  },
);

interface Props {
  product: ProductDetails;
  ref: RefObject<ReferenceType | null> & RefObject<ReferenceType | null>;
  setFloatingReference: (node: HTMLElement | null) => void;
  floatingStyles: CSSProperties;
  onCancel: () => void;
  onClickAddToCart: () => void;
  onSelectVariant: (variant: ProductVariant) => void;
}
const AddToCartPopover = ({
  product,
  ref,
  setFloatingReference,
  floatingStyles,
  onSelectVariant,
  onCancel,
  onClickAddToCart,
}: Props) => {
  useOnClickOutside(ref as RefObject<HTMLElement | null>, onCancel);

  return (
    <Box
      zIndex="popover"
      bg="surface.background"
      boxShadow="sm"
      borderRadius="md"
      ref={setFloatingReference}
      style={floatingStyles}
    >
      <Flex p="2" w="full" flexDirection="column" gap="2">
        <VariantSelection
          product={product}
          onSelect={onSelectVariant}
          defaultVariantId={product.id}
        />
        <Button size="2xs" onClick={onClickAddToCart} alignSelf="flex-end">
          Done
        </Button>
      </Flex>
    </Box>
  );
};

export default memo(AddToCartPopover);
